import type { ProviderTokens } from "./providerAuth.ts";
import type { ProviderRefreshCoordinator } from "./providerAuthClient.ts";

const protocolVersion = 1;
const peerWaitMs = 180;

type RefreshMessage = {
  version: 1;
  type: "rotated" | "sync-request" | "sync-response";
  requestId: string | null;
  accountUuid: string;
  expectedRefreshToken: string;
  tokens: ProviderTokens | null;
};

type PendingPeer = {
  accountUuid: string;
  expectedRefreshToken: string;
  resolve: (tokens: ProviderTokens | null) => void;
  timer: ReturnType<typeof setTimeout>;
};

function validTokens(value: unknown): value is ProviderTokens {
  if (!value || typeof value !== "object") return false;
  const tokens = value as Partial<ProviderTokens>;
  return Boolean(
    typeof tokens.accessToken === "string" && tokens.accessToken &&
      typeof tokens.refreshToken === "string" && tokens.refreshToken &&
      typeof tokens.expiresAt === "number" && Number.isFinite(tokens.expiresAt),
  );
}

function parseMessage(value: unknown): RefreshMessage | null {
  if (!value || typeof value !== "object") return null;
  const message = value as Partial<RefreshMessage>;
  if (
    message.version !== protocolVersion ||
    !["rotated", "sync-request", "sync-response"].includes(message.type ?? "") ||
    typeof message.accountUuid !== "string" ||
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(message.accountUuid) ||
    typeof message.expectedRefreshToken !== "string" ||
    !message.expectedRefreshToken ||
    !(message.requestId === null || typeof message.requestId === "string")
  ) {
    return null;
  }
  if (message.type === "sync-request") {
    if (!message.requestId || message.tokens !== null) return null;
  } else if (!validTokens(message.tokens)) {
    return null;
  }
  return message as RefreshMessage;
}

/// Coordinates rotating refresh tokens without persisting bearer material in
/// localStorage. BroadcastChannel is origin-scoped and used only for transient
/// handoff; Web Locks serializes the normal path when the browser supports it.
export class BrowserProviderRefreshCoordinator implements ProviderRefreshCoordinator {
  private readonly channel: BroadcastChannel | null;
  private readonly lockScope: string;
  private readonly latest = new Map<string, ProviderTokens>();
  private readonly pending = new Map<string, PendingPeer>();

  constructor(scope: string) {
    this.lockScope = scope.replace(/[^A-Za-z0-9._-]/g, "_");
    this.channel = typeof BroadcastChannel === "function"
      ? new BroadcastChannel(`wenlix-provider-refresh-v1:${this.lockScope}`)
      : null;
    this.channel?.addEventListener("message", (event) => this.onMessage(event.data));
  }

  remember(accountUuid: string, tokens: ProviderTokens): void {
    if (validTokens(tokens)) this.latest.set(accountUuid, tokens);
  }

  forget(accountUuid: string): void {
    this.latest.delete(accountUuid);
  }

  close(): void {
    for (const pending of this.pending.values()) {
      clearTimeout(pending.timer);
      pending.resolve(null);
    }
    this.pending.clear();
    this.channel?.close();
  }

  async coordinate(
    accountUuid: string,
    expectedRefreshToken: string,
    exchange: () => Promise<ProviderTokens>,
  ): Promise<ProviderTokens> {
    const run = () => this.coordinateInsideLock(
      accountUuid,
      expectedRefreshToken,
      exchange,
    );
    const locks = typeof navigator === "undefined" ? null : navigator.locks;
    if (!locks) return run();
    return await locks.request(
      `wenlix-provider-refresh:${this.lockScope}:${accountUuid}`,
      { mode: "exclusive" },
      run,
    );
  }

  private async coordinateInsideLock(
    accountUuid: string,
    expectedRefreshToken: string,
    exchange: () => Promise<ProviderTokens>,
  ): Promise<ProviderTokens> {
    const remembered = this.successor(accountUuid, expectedRefreshToken);
    if (remembered) return remembered;

    const peer = await this.requestPeer(accountUuid, expectedRefreshToken);
    if (peer) return peer;

    try {
      const tokens = await exchange();
      this.remember(accountUuid, tokens);
      this.post({
        version: protocolVersion,
        type: "rotated",
        requestId: null,
        accountUuid,
        expectedRefreshToken,
        tokens,
      });
      return tokens;
    } catch (error) {
      const recovered = await this.requestPeer(accountUuid, expectedRefreshToken);
      if (recovered) return recovered;
      throw error;
    }
  }

  private successor(
    accountUuid: string,
    expectedRefreshToken: string,
  ): ProviderTokens | null {
    const tokens = this.latest.get(accountUuid);
    if (!tokens || tokens.refreshToken === expectedRefreshToken) return null;
    return tokens;
  }

  private requestPeer(
    accountUuid: string,
    expectedRefreshToken: string,
  ): Promise<ProviderTokens | null> {
    if (!this.channel) return Promise.resolve(null);
    const requestId = crypto.randomUUID();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        resolve(null);
      }, peerWaitMs);
      this.pending.set(requestId, {
        accountUuid,
        expectedRefreshToken,
        resolve,
        timer,
      });
      this.post({
        version: protocolVersion,
        type: "sync-request",
        requestId,
        accountUuid,
        expectedRefreshToken,
        tokens: null,
      });
    });
  }

  private onMessage(raw: unknown): void {
    const message = parseMessage(raw);
    if (!message) return;
    if (message.type === "rotated") {
      this.remember(message.accountUuid, message.tokens!);
      for (const [requestId, pending] of this.pending) {
        if (
          pending.accountUuid !== message.accountUuid ||
          pending.expectedRefreshToken !== message.expectedRefreshToken
        ) {
          continue;
        }
        clearTimeout(pending.timer);
        this.pending.delete(requestId);
        pending.resolve(message.tokens);
      }
      return;
    }
    if (message.type === "sync-request") {
      const tokens = this.successor(
        message.accountUuid,
        message.expectedRefreshToken,
      );
      if (!tokens) return;
      this.post({
        version: protocolVersion,
        type: "sync-response",
        requestId: message.requestId,
        accountUuid: message.accountUuid,
        expectedRefreshToken: message.expectedRefreshToken,
        tokens,
      });
      return;
    }

    const requestId = message.requestId;
    if (!requestId) return;
    const pending = this.pending.get(requestId);
    if (
      !pending ||
      pending.accountUuid !== message.accountUuid ||
      pending.expectedRefreshToken !== message.expectedRefreshToken
    ) {
      return;
    }
    clearTimeout(pending.timer);
    this.pending.delete(requestId);
    this.remember(message.accountUuid, message.tokens!);
    pending.resolve(message.tokens);
  }

  private post(message: RefreshMessage): void {
    this.channel?.postMessage(message);
  }
}
